import { Spin, Empty, Typography, Button, Modal, Form, Input, Table, Select, Space, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, LockOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDateOnly } from '../../helpers/date';
import './staffTable.css';

const { Option } = Select;

interface StaffTableDataType {
  key: number;
  intPkStaffID: number;
  staffName: string;
  staffEmail: string;
  staffRole: string;
  isActive: number;
  staffCreatedAt: string;
}

const StaffComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [createForm] = Form.useForm();
  const [updateProfileForm] = Form.useForm();
  const [updatePasswordForm] = Form.useForm();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState(false);
  const [isUpdatePasswordModalOpen, setIsUpdatePasswordModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffTableDataType | null>(null);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('staff/all');

  const [
    createStaffData,
    createStaffResponse,
    createStaffError,
    createStaffLoading,
    createStaffMessage,
    createStaffErrorCount,
  ] = useAxiosPost<ResponseProp>('staff/create');

  const [
    updateProfileData,
    updateProfileResponse,
    updateProfileError,
    updateProfileLoading,
    updateProfileMessage,
    updateProfileErrorCount,
  ] = useAxiosPost<ResponseProp>('staff/update-profile');

  const [
    updatePasswordData,
    updatePasswordResponse,
    updatePasswordError,
    updatePasswordLoading,
    updatePasswordMessage,
    updatePasswordErrorCount,
  ] = useAxiosPost<ResponseProp>('staff/update-password');

  const [allStaff, setAllStaff] = useState<StaffTableDataType[]>([]);
  const postApiDataRef = useRef(postApiData);
  const hasProcessedCreateSuccess = useRef(false);
  const lastShownUpdateProfileResponse = useRef<any>(null);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0, update: 0 });

  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        const staffWithKeys = responseData.map((staff: any) => ({
          ...staff,
          key: staff.intPkStaffID,
        }));
        setAllStaff(staffWithKeys);
      } else {
        setAllStaff([]);
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.api !== apiErrorCount) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification('error', 'Failed to Load Staff', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create staff success
  useEffect(() => {
    if (createStaffResponse !== null && !hasProcessedCreateSuccess.current) {
      hasProcessedCreateSuccess.current = true;
      openNotification('success', 'Staff Created', 'Staff created successfully');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createStaffResponse]);

  useEffect(() => {
    if (createStaffResponse === null) {
      hasProcessedCreateSuccess.current = false;
    }
  }, [createStaffResponse]);

  useEffect(() => {
    if (
      createStaffError !== null &&
      createStaffErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createStaffErrorCount
    ) {
      lastProcessedErrorCount.current.create = createStaffErrorCount;
      openNotification('error', 'Failed to Create Staff', createStaffError);
    }
  }, [createStaffError, createStaffErrorCount, openNotification]);

  // When response is cleared (new request started), reset so next response will show notification
  useEffect(() => {
    if (updateProfileResponse === null) {
      lastShownUpdateProfileResponse.current = null;
    }
  }, [updateProfileResponse]);

  // Handle update profile success – show for every new response so second update also shows
  useEffect(() => {
    if (
      updateProfileResponse !== null &&
      updateProfileResponse !== lastShownUpdateProfileResponse.current
    ) {
      lastShownUpdateProfileResponse.current = updateProfileResponse;
      openNotification('success', 'Profile Updated', 'Staff profile updated successfully');
      setIsUpdateProfileModalOpen(false);
      updateProfileForm.resetFields();
      setSelectedStaff(null);
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [updateProfileResponse]);

  useEffect(() => {
    if (
      updateProfileError !== null &&
      updateProfileErrorCount !== 0 &&
      lastProcessedErrorCount.current.update !== updateProfileErrorCount
    ) {
      lastProcessedErrorCount.current.update = updateProfileErrorCount;
      openNotification('error', 'Failed to Update Profile', updateProfileError);
    }
  }, [updateProfileError, updateProfileErrorCount, openNotification]);

  // Handle update password success
  useEffect(() => {
    if (updatePasswordResponse !== null) {
      openNotification('success', 'Password Updated', 'Staff password updated successfully');
      setIsUpdatePasswordModalOpen(false);
      updatePasswordForm.resetFields();
      setSelectedStaff(null);
    }
  }, [updatePasswordResponse]);

  useEffect(() => {
    if (updatePasswordError !== null && updatePasswordErrorCount !== 0) {
      openNotification('error', 'Failed to Update Password', updatePasswordError);
    }
  }, [updatePasswordError, updatePasswordErrorCount, openNotification]);

  const showCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateCancel = () => {
    if (!createStaffLoading) {
      setIsCreateModalOpen(false);
      createForm.resetFields();
    }
  };

  const handleCreate = (values: any) => {
    const { staffName, staffEmail, staffPassword, confirmPassword, staffRole } = values;
    createStaffData({
      staffName: staffName.trim(),
      staffEmail: staffEmail.trim().toLowerCase(),
      staffPassword,
      confirmPassword,
      staffRole,
    });
  };

  const handleUpdateProfile = (values: any) => {
    if (!selectedStaff) return;
    lastShownUpdateProfileResponse.current = null; // so next response is treated as new and notification shows
    const { staffName, staffEmail } = values;
    updateProfileData({
      intPkStaffID: selectedStaff.intPkStaffID,
      staffName: staffName.trim(),
      staffEmail: staffEmail.trim().toLowerCase(),
    });
  };

  const handleUpdatePassword = (values: any) => {
    if (!selectedStaff) return;
    const { newPassword, confirmPassword } = values;
    if (newPassword !== confirmPassword) {
      openNotification('error', 'Validation Error', 'New and confirm Password Mismatch');
      return;
    }
    updatePasswordData({
      intPkStaffID: selectedStaff.intPkStaffID,
      newPassword,
      confirmPassword,
    });
  };

  const showUpdateProfileModal = (staff: StaffTableDataType) => {
    setSelectedStaff(staff);
    updateProfileForm.setFieldsValue({
      staffName: staff.staffName,
      staffEmail: staff.staffEmail,
    });
    setIsUpdateProfileModalOpen(true);
  };

  const showUpdatePasswordModal = (staff: StaffTableDataType) => {
    setSelectedStaff(staff);
    setIsUpdatePasswordModalOpen(true);
  };

  const handleUpdateProfileCancel = () => {
    if (!updateProfileLoading) {
      setIsUpdateProfileModalOpen(false);
      updateProfileForm.resetFields();
      setSelectedStaff(null);
    }
  };

  const handleUpdatePasswordCancel = () => {
    if (!updatePasswordLoading) {
      setIsUpdatePasswordModalOpen(false);
      updatePasswordForm.resetFields();
      setSelectedStaff(null);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'red';
      case 'MANAGER':
        return 'blue';
      case 'SUPPORT':
        return 'green';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<StaffTableDataType> = [
    {
      title: 'Name',
      dataIndex: 'staffName',
      key: 'staffName',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Email',
      dataIndex: 'staffEmail',
      key: 'staffEmail',
    },
    {
      title: 'Role',
      dataIndex: 'staffRole',
      key: 'staffRole',
      render: (role: string) => <Tag color={getRoleColor(role)}>{role}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: number) => (
        <Typography.Text type={isActive === 1 ? 'success' : 'secondary'}>
          {isActive === 1 ? 'Active' : 'Inactive'}
        </Typography.Text>
      ),
    },
    {
      title: 'Date Created',
      dataIndex: 'staffCreatedAt',
      key: 'staffCreatedAt',
      render: (date: string) => {
        if (!date) return '-';
        return formatDateOnly(date);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              showUpdateProfileModal(record);
            }}
          >
            Edit Profile
          </Button>
          <Button
            type="link"
            icon={<LockOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              showUpdatePasswordModal(record);
            }}
          >
            Change Password
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal} size="large">
          Create Staff
        </Button>
      </div>

      {/* Create Staff Modal */}
      <Modal
        title="Create New Staff"
        open={isCreateModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
        maskClosable={!createStaffLoading}
        closable={!createStaffLoading}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Name</span>}
            name="staffName"
            rules={[
              {
                required: true,
                message: 'Please enter staff name',
              },
              {
                min: 1,
                message: 'Name cannot be empty',
              },
              {
                max: 100,
                message: 'Name must be less than 100 characters',
              },
            ]}
          >
            <Input placeholder="Enter staff name" disabled={createStaffLoading} maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Email</span>}
            name="staffEmail"
            rules={[
              {
                required: true,
                message: 'Please enter email',
              },
              {
                type: 'email',
                message: 'Please enter a valid email',
              },
            ]}
          >
            <Input placeholder="Enter email" disabled={createStaffLoading} type="email" />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Password</span>}
            name="staffPassword"
            rules={[
              {
                required: true,
                message: 'Please enter password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
            ]}
          >
            <Input.Password placeholder="Enter password" disabled={createStaffLoading} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Confirm Password</span>}
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: 'Please confirm password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('staffPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" disabled={createStaffLoading} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Role</span>}
            name="staffRole"
            rules={[
              {
                required: true,
                message: 'Please select a role',
              },
            ]}
          >
            <Select placeholder="Select role" disabled={createStaffLoading}>
              <Option value="MANAGER">MANAGER</Option>
              <Option value="SUPPORT">SUPPORT</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCreateCancel} style={{ marginRight: '8px' }} disabled={createStaffLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={createStaffLoading} disabled={createStaffLoading}>
              {createStaffLoading ? 'Creating...' : 'Create Staff'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Update Profile Modal */}
      <Modal
        title="Update Staff Profile"
        open={isUpdateProfileModalOpen}
        onCancel={handleUpdateProfileCancel}
        footer={null}
        width={600}
        maskClosable={!updateProfileLoading}
        closable={!updateProfileLoading}
      >
        <Form form={updateProfileForm} layout="vertical" onFinish={handleUpdateProfile} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Name</span>}
            name="staffName"
            rules={[
              {
                required: true,
                message: 'Please enter staff name',
              },
              {
                min: 1,
                message: 'Name cannot be empty',
              },
              {
                max: 100,
                message: 'Name must be less than 100 characters',
              },
            ]}
          >
            <Input placeholder="Enter staff name" disabled={updateProfileLoading} maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Email</span>}
            name="staffEmail"
            rules={[
              {
                required: true,
                message: 'Please enter email',
              },
              {
                type: 'email',
                message: 'Please enter a valid email',
              },
            ]}
          >
            <Input placeholder="Enter email" disabled={updateProfileLoading} type="email" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleUpdateProfileCancel} style={{ marginRight: '8px' }} disabled={updateProfileLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updateProfileLoading} disabled={updateProfileLoading}>
              {updateProfileLoading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Update Password Modal */}
      <Modal
        title="Update Staff Password"
        open={isUpdatePasswordModalOpen}
        onCancel={handleUpdatePasswordCancel}
        footer={null}
        width={600}
        maskClosable={!updatePasswordLoading}
        closable={!updatePasswordLoading}
      >
        <Form form={updatePasswordForm} layout="vertical" onFinish={handleUpdatePassword} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>New Password</span>}
            name="newPassword"
            rules={[
              {
                required: true,
                message: 'Please enter new password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
            ]}
          >
            <Input.Password placeholder="Enter new password" disabled={updatePasswordLoading} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Confirm New Password</span>}
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: 'Please confirm new password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" disabled={updatePasswordLoading} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button
              onClick={handleUpdatePasswordCancel}
              style={{ marginRight: '8px' }}
              disabled={updatePasswordLoading}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updatePasswordLoading} disabled={updatePasswordLoading}>
              {updatePasswordLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading staff...</Typography>
        </div>
      ) : apiError && !apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description={<Typography.Text type="danger">{apiError || 'Failed to load staff'}</Typography.Text>} />
        </div>
      ) : allStaff.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No staff found. Create your first staff member!" />
        </div>
      ) : (
        <div className="table-container-custom">
          <Table
            className="antd-table-striped"
            columns={columns}
            dataSource={allStaff}
            pagination={false}
            loading={apiLoading}
          />
        </div>
      )}
    </>
  );
};
export default StaffComponent;
